import {
  BarChart3,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  GraduationCap,
  LayoutDashboard,
  ListChecks,
  LockKeyhole,
  Settings2,
  ShieldCheck
} from "lucide-react";
import type { AttemptEvent, ChildProfile, ContentNode, Surface, TrustEvent } from "../types";
import { ChildLesson } from "./surfaces/ChildLesson";
import { ContentStudio } from "./surfaces/ContentStudio";
import { ParentDashboard } from "./surfaces/ParentDashboard";
import { TrustDashboard } from "./surfaces/TrustDashboard";

const surfaceMeta: Record<Surface, { label: string; description: string; icon: typeof LayoutDashboard }> = {
  parent: {
    label: "Parent",
    description: "Overview and progress",
    icon: LayoutDashboard
  },
  child: {
    label: "Child",
    description: "Lesson in progress",
    icon: BookOpen
  },
  studio: {
    label: "Studio",
    description: "Create and QA",
    icon: ClipboardCheck
  },
  trust: {
    label: "Trust",
    description: "Safety and privacy",
    icon: ShieldCheck
  }
};

interface ProductConsoleProps {
  activeSurface: Surface;
  attempts: AttemptEvent[];
  children: ChildProfile[];
  nodes: ContentNode[];
  recommendedNode: ContentNode;
  selectedChild: ChildProfile;
  selectedChildId: string;
  setActiveSurface: (surface: Surface) => void;
  setSelectedChildId: (childId: string) => void;
  trustEvents: TrustEvent[];
  onAttempt: (correct: boolean, hints: number) => void;
  onNodeStatus: (nodeId: string, status: ContentNode["status"]) => void;
}

export function ProductConsole({
  activeSurface,
  attempts,
  children,
  nodes,
  recommendedNode,
  selectedChild,
  selectedChildId,
  setActiveSurface,
  setSelectedChildId,
  trustEvents,
  onAttempt,
  onNodeStatus
}: ProductConsoleProps) {
  return (
    <section className="console-section" id="console" aria-labelledby="console-title">
      <div className="section-heading">
        <div>
          <h2 id="console-title">One safe learning layer inside Nesty.</h2>
          <p>
            This build focuses on the learning application itself: parent insight, child lessons, content QA,
            and learning-safety oversight that can sit alongside the existing Nesty tablet system.
          </p>
        </div>
        <div className="console-status" aria-label="Release readiness">
          <CheckCircle2 size={20} aria-hidden="true" />
          PWA-ready MVP
        </div>
      </div>

      <div className="product-console">
        <div className="surface-tabs" role="tablist" aria-label="Nesty Learning surfaces">
          {(Object.keys(surfaceMeta) as Surface[]).map((surface) => {
            const meta = surfaceMeta[surface];
            const Icon = meta.icon;
            const active = activeSurface === surface;
            return (
              <button
                aria-selected={active}
                className={active ? "surface-tab is-active" : "surface-tab"}
                key={surface}
                onClick={() => setActiveSurface(surface)}
                role="tab"
                type="button"
              >
                <span className="surface-icon" aria-hidden="true">
                  <Icon size={22} />
                </span>
                <span>
                  <strong>{meta.label}</strong>
                  <small>{meta.description}</small>
                </span>
              </button>
            );
          })}
        </div>

        <div className="console-body" role="tabpanel">
          {activeSurface === "parent" && (
            <ParentDashboard
              children={children}
              recommendedNode={recommendedNode}
              selectedChild={selectedChild}
              selectedChildId={selectedChildId}
              setActiveSurface={setActiveSurface}
              setSelectedChildId={setSelectedChildId}
            />
          )}
          {activeSurface === "child" && (
            <ChildLesson child={selectedChild} contentNode={recommendedNode} onAttempt={onAttempt} />
          )}
          {activeSurface === "studio" && (
            <ContentStudio nodes={nodes} onNodeStatus={onNodeStatus} selectedChild={selectedChild} />
          )}
          {activeSurface === "trust" && (
            <TrustDashboard attempts={attempts} children={children} trustEvents={trustEvents} />
          )}
        </div>
      </div>

      <div className="console-pillars" aria-label="Blueprint principles">
        <span>
          <GraduationCap size={18} aria-hidden="true" /> Reading and maths first
        </span>
        <span>
          <BarChart3 size={18} aria-hidden="true" /> Skill-based progression
        </span>
        <span>
          <ListChecks size={18} aria-hidden="true" /> Cumulative review
        </span>
        <span>
          <Settings2 size={18} aria-hidden="true" /> Nesty-ready integration
        </span>
        <span>
          <Eye size={18} aria-hidden="true" /> Plain-English reports
        </span>
        <span>
          <LockKeyhole size={18} aria-hidden="true" /> Bounded by design
        </span>
      </div>
    </section>
  );
}
